import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, User as UserIcon } from "lucide-react";
import { useAuth } from "../../../contexts/AuthContext";
import { useAuthModal } from "../../../contexts/AuthModalContext";
import * as Sentry from "@sentry/react";
import { AuthError } from "../../../errors/sentryErrors";
import { useMyProfile } from "../../../hooks/useProfile";
import { NotificationBell } from "../../community/notifications/NotificationBell";
import { trackEvent } from "../../../utils/analytics";
import { FUNNEL_URL } from "../../../config";

const VISA_URL = import.meta.env.VITE_VISA_URL;

const HOME_NAV_ITEMS = [
  { label: "Romaya AI Trip Planner", href: "/", highlighted: true },
  { label: "Packages", href: `${FUNNEL_URL}/search`, external: true },
  { label: "Visa", href: VISA_URL, external: true, newTab: true, badge: "New" },
  { label: "Community", href: "/community", external: false },
  { label: "Blogs", href: `${FUNNEL_URL}/blogs`, external: true },
];

function HamburgerIcon({ open }) {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      {open ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      )}
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg
      className="w-4 h-4 mr-2"
      fill="none"
      stroke="#f97316"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
      />
    </svg>
  );
}

function WalletIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M3 7a2 2 0 012-2h12a2 2 0 012 2v1" />
      <rect x="3" y="8" width="18" height="11" rx="2" />
      <circle cx="16.5" cy="13.5" r="1.25" fill="currentColor" stroke="none" />
    </svg>
  );
}

// Header-level version of the credits badge that used to sit inline on the
// dashboard page — moved up here so it's visible from every app route, not
// just /dashboard. Same figure (profile.stats.credits), same destination
// (/account?tab=wallet), just smaller to fit the header row.
function CreditsBadge() {
  const { data: myProfile } = useMyProfile();
  const credits = myProfile?.stats?.credits;

  if (credits === undefined || credits === null) return null;

  return (
    <Link
      to="/account?tab=wallet"
      title="My RoamPoints"
      className="hidden sm:inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200/80 pl-1.5 pr-3 py-1 hover:bg-amber-100 transition-colors"
    >
      <span className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-200 to-amber-400 text-amber-900 flex items-center justify-center shrink-0 ring-1 ring-amber-500/15">
        <WalletIcon className="w-3.5 h-3.5" />
      </span>
      <span className="text-sm font-bold text-gray-900 tabular-nums leading-none">
        {credits.toLocaleString()}
      </span>
    </Link>
  );
}

function UserAvatar({ user }) {
  const fullName = user.name || user.email?.split('@')[0] || 'User';
  const firstName = fullName.split(' ')[0];
  const initial = firstName.charAt(0).toUpperCase();

  // The handle lookup (and its loading state) is gone with the /u/:handle link:
  // the dropdown needs no handle, so the avatar no longer waits on that request
  // before becoming clickable.

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Escape closes and hands focus back to the avatar. Focus is restored only
    // on this path: a click outside has already moved focus somewhere the user
    // chose, and following a menu item unmounts the page anyway — pulling focus
    // back to the trigger in either case would be the surprising thing.
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Own avatar opens a quick-access menu (dashboard, account settings) —
          the public community profile is reachable from the account page and
          from any post byline instead. Sign out lives in the account
          sidebar's Logout item, not here. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="hover:opacity-80 transition-opacity"
        title="My account"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {user.image ? (
          <img
            src={user.image}
            alt={firstName}
            className="w-8 h-8 rounded-full object-cover border-2 border-teal-600"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-sm">
            {initial}
          </div>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
          role="menu"
          aria-label="My account"
        >
          {/* A menu may own nothing but menuitems, so the two non-item wrappers
              inside it — this static name/email header, and the grouping div
              below — are marked `role="none"`. That drops them from the
              accessibility tree's structure without hiding their text, leaving
              the menu owning exactly its two menuitems. */}
          <div className="p-3 border-b border-gray-100" role="none">
            <p className="text-sm font-semibold text-gray-900 truncate">{fullName}</p>
            {user.email && <p className="text-xs text-gray-500 truncate">{user.email}</p>}
          </div>
          <div className="py-2" role="none">
            <Link
              to="/dashboard"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <LayoutDashboard className="w-4 h-4 text-gray-500" />
              Dashboard
            </Link>
            <Link
              to="/account"
              role="menuitem"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <UserIcon className="w-4 h-4 text-gray-500" />
              My Account
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

function NewBadge() {
  return (
    <span className="absolute -top-0 -right-7 px-1.5 py-px rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[9px] font-bold uppercase tracking-wider leading-none shadow-md ring-2 ring-white animate-pulse-soft">
      New
    </span>
  );
}

function NavLink({ item, active }) {
  if (item.highlighted) {
    return (
      <Link
        to={item.href}
        className="inline-flex items-center whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold text-teal-800 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 shadow-sm hover:shadow-md hover:border-teal-300 hover:from-teal-100 hover:to-cyan-100 transition-all"
      >
        <SparkleIcon />
        {item.label}
      </Link>
    );
  }

  const padRight = item.badge ? "pr-3 mr-8" : "pr-3";

  if (item.external) {
    return (
      <a
        href={item.href}
        target={item.newTab ? "_blank" : undefined}
        rel={item.newTab ? "noopener noreferrer" : undefined}
        onClick={() => trackEvent("nav_link_clicked", { location: "navbar", label: item.label.toLowerCase() })}
        className={`relative inline-flex items-center whitespace-nowrap py-2 pl-3 ${padRight} text-sm font-medium text-gray-700 hover:text-gray-900 transition-all group`}
      >
        {item.label}
        {item.badge && <NewBadge />}
        <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-teal-600 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
      </a>
    );
  }

  return (
    <Link
      to={item.href}
      className={`relative inline-flex items-center whitespace-nowrap py-2 pl-3 ${padRight} text-sm font-medium text-gray-700 hover:text-gray-900 transition-all ${active ? "text-teal-600" : ""} group`}
    >
      {item.label}
      {item.badge && <NewBadge />}
      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-teal-600 scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></span>
    </Link>
  );
}

export default function Navbar({ rightAction }) {
  const location = useLocation();
  const pathname = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef(null);

  const { user, isAuthenticated, loading, logout } = useAuth();
  const { open: openAuthModal } = useAuthModal();

  const isAppRoute = pathname.startsWith("/dashboard") ||
                     pathname.startsWith("/plan") ||
                     pathname.startsWith("/itinerary") ||
                     pathname.startsWith("/auth");

  const NAV_ITEMS = isAppRoute
    ? HOME_NAV_ITEMS.filter((item) => item.href !== "/")
    : HOME_NAV_ITEMS;

  const showSignIn = !isAppRoute && !isAuthenticated && !loading;

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen]);

  const isActive = (href) => {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  const handleSignIn = () => {
    openAuthModal();
  };

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "auth");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new AuthError(err instanceof Error ? err.message : String(err)));
      });
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${isAppRoute ? 'bg-white shadow-sm border-b border-gray-200' : 'bg-white/70 backdrop-blur-md border-b border-gray-200/30 shadow-sm'}`}>
      <nav aria-label="Primary" className="w-full">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="h-16 lg:h-20 flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center flex-shrink-0"
                aria-label="RoamMyWay home"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <img
                  src="/RoamMyWay.png"
                  alt="RoamMyWay"
                  className="h-10 lg:h-14 w-auto object-contain"
                />
              </Link>
            </div>


            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-4">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.href} item={item} active={isActive(item.href)} />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {rightAction ? (
                <div className="hidden lg:flex">{rightAction}</div>
              ) : (
                <>
                  {isAuthenticated && user && (
                    <div className="hidden lg:flex items-center gap-4">
                      <CreditsBadge />
                      <NotificationBell />
                      <UserAvatar user={user} />
                    </div>
                  )}

                  {showSignIn && (
                    <button
                      onClick={handleSignIn}
                      className="hidden lg:inline-flex items-center justify-center rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-teal-700"
                    >
                      Sign In
                    </button>
                  )}

                  {loading && (
                    <div className="hidden lg:flex items-center">
                      <div className="w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </>
              )}

              {isAuthenticated && user && (
                <div className="lg:hidden mr-2 flex items-center">
                  <NotificationBell />
                </div>
              )}
              <div ref={menuRef} className="lg:hidden flex items-center">
                <button
                  type="button"
                  className="p-2 rounded-lg text-gray-700 hover:bg-gray-100/50 transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-expanded={mobileMenuOpen}
                  aria-label="Toggle navigation menu"
                >
                  <HamburgerIcon open={mobileMenuOpen} />
                </button>

                {mobileMenuOpen && (
                  <div className="absolute right-4 left-4 top-full mt-2 rounded-xl shadow-lg overflow-hidden z-50 bg-white border border-gray-200">
                    <div className="flex flex-col p-3 gap-1">
                      {isAuthenticated && user && (
                        <div className="px-4 py-3 border-b border-gray-100 mb-2">
                          <div className="flex items-center gap-3">
                            {user.image ? (
                              <img
                                src={user.image}
                                alt={user.name || 'User'}
                                className="w-10 h-10 rounded-full object-cover border-2 border-teal-600"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold">
                                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {user.name || user.email?.split('@')[0]}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{user.email}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {NAV_ITEMS.map((item) => (
                        item.external ? (
                          <a
                            key={item.href}
                            href={item.href}
                            target={item.newTab ? "_blank" : undefined}
                            rel={item.newTab ? "noopener noreferrer" : undefined}
                            onClick={() => { trackEvent("nav_link_clicked", { location: "navbar", label: item.label.toLowerCase() }); setMobileMenuOpen(false); }}
                            className={`flex items-center justify-between gap-2 px-4 py-3 rounded-lg text-base font-medium transition-colors text-gray-700 hover:text-orange-500 hover:bg-gray-50`}
                          >
                            <span>{item.label}</span>
                            {item.badge && (
                              <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] font-bold uppercase tracking-wider leading-none shadow-sm">
                                {item.badge}
                              </span>
                            )}
                          </a>
                        ) : (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                              item.highlighted
                                ? "text-teal-700 bg-gray-50 border border-gray-200"
                                : "text-gray-700 hover:text-orange-500 hover:bg-gray-50"
                            } ${isActive(item.href) ? "text-orange-500 bg-orange-50" : ""}`}
                          >
                            {item.label}
                          </Link>
                        )
                      ))}

                      {/* The desktop avatar dropdown's destinations. Mobile has
                          no avatar trigger, so without these two the only way
                          to reach the dashboard or account settings on a phone
                          is to type the URL. Signed-in only, matching the
                          dropdown. */}
                      {isAuthenticated && user && (
                        <div className="mt-1 pt-2 border-t border-gray-100 flex flex-col gap-1">
                          <Link
                            to="/dashboard"
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors text-gray-700 hover:text-orange-500 hover:bg-gray-50 ${
                              isActive("/dashboard") ? "text-orange-500 bg-orange-50" : ""
                            }`}
                          >
                            <LayoutDashboard className="w-5 h-5 text-gray-500" />
                            Dashboard
                          </Link>
                          <Link
                            to="/account"
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors text-gray-700 hover:text-orange-500 hover:bg-gray-50 ${
                              isActive("/account") ? "text-orange-500 bg-orange-50" : ""
                            }`}
                          >
                            <UserIcon className="w-5 h-5 text-gray-500" />
                            My Account
                          </Link>
                        </div>
                      )}

                      {rightAction ? (
                        <div className="mt-2" onClick={() => setMobileMenuOpen(false)}>{rightAction}</div>
                      ) : (
                        <>
                          {isAuthenticated ? (
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                handleSignOut();
                              }}
                              disabled={signingOut}
                              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-red-50 px-4 py-3 text-base font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-80 disabled:cursor-not-allowed"
                            >
                              {signingOut ? (
                                <>
                                  <span className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                  Signing out
                                </>
                              ) : (
                                "Sign Out"
                              )}
                            </button>
                          ) : showSignIn && (
                            <button
                              onClick={() => {
                                setMobileMenuOpen(false);
                                handleSignIn();
                              }}
                              className="mt-2 block text-center rounded-full bg-teal-600 px-4 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-teal-700"
                            >
                              Sign In
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}
